// frontend/src/pages/admin/CreateResearchPage.tsx
import { useState, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { ArrowLeft, Save, Loader2, AlertCircle, CheckCircle, UploadCloud } from 'lucide-react';
import { createResearchItem } from '@/api/researchService';
import { getAllTags } from '@/api/tagService';
import { type ResearchCreateJsonPayload, type TagPublic } from '@/types';

const CreateResearchPage = () => {
  const navigate = useNavigate();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'draft' | 'published' | 'archived'>('draft');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string>('');

  const [allTags, setAllTags] = useState<TagPublic[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const tagsData = await getAllTags();
        setAllTags(tagsData);
      } catch (err) {
        console.error("Failed to fetch tags:", err);
        setError("Could not load tags. Please try refreshing or creating tags first.");
      }
    };
    fetchTags();
  }, []);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      console.log("[DEBUG CreateResearchPage] File selected:", file);
      if (file.type === "application/pdf") {
        setPdfFile(file);
        setPdfFileName(file.name);
        setError(null); 
      } else {
        setPdfFile(null);
        setPdfFileName('');
        setError("Invalid file type. Please upload a PDF.");
      }
    } else {
      console.log("[DEBUG CreateResearchPage] No file selected or files array empty.");
      setPdfFile(null);
      setPdfFileName('');
    }
  };

  const handleTagSelection = (tagIdString: string) => {
    const id = parseInt(tagIdString, 10);
    setSelectedTagIds(prev =>
      prev.includes(id) ? prev.filter(tId => tId !== id) : [...prev, id]
    );
  };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    if (!title || !pdfFile) {
      setError("Title and a PDF file are required.");
      setIsSubmitting(false);
      return;
    }
    
    const formData = new FormData();
    
    // Append fields exactly as the backend expects
    formData.append('title', title);
    formData.append('status', status);
    formData.append('pdf_file', pdfFile);

    if (description) {
      formData.append('description', description);
    }
    
    // --- THIS IS THE FIX ---
    // The backend expects the field `tag_ids_json` with a JSON string array.
    if (selectedTagIds.length > 0) {
      formData.append('tag_ids_json', JSON.stringify(selectedTagIds));
    }

    try {
      const newResearchItem = await createResearchItem(formData);
      setSuccessMessage(`Research item "${newResearchItem.title}" created successfully! Redirecting...`);
      
      setTimeout(() => {
        navigate('/admin/research');
      }, 2000);
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail || err.message || "Failed to create research item.";
      setError(typeof errorDetail === 'string' ? errorDetail : JSON.stringify(errorDetail));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 md:py-12">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate(-1)} className="text-primary hover:text-primary/80 cursor-pointer">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Manage Research
        </Button>
      </div>

      <Card className="max-w-3xl mx-auto bg-card border-border/30 shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl md:text-3xl text-foreground">Upload New Research</CardTitle>
          <CardDescription className="text-muted-foreground">Fill in the details and upload the PDF document.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            {error && (
              <div className="p-3 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" /> {error}
              </div>
            )}
            {successMessage && (
              <div className="p-3 bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/30 rounded-md flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" /> {successMessage}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="title" className="text-foreground">Title <span className="text-destructive">*</span></Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter research title"
                required
                className="!bg-input border-border focus:border-primary focus:ring-primary"
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground">Description (Optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief summary of the research paper..."
                rows={4}
                className="!bg-input border-border focus:border-primary focus:ring-primary min-h-[100px]"
                disabled={isSubmitting}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="pdfFile" className="text-foreground">PDF Document <span className="text-destructive">*</span></Label>
              <div className="flex items-center space-x-3">
                <Input
                  id="pdfFile"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  required
                  className="!bg-input border-border focus:border-primary focus:ring-primary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                  disabled={isSubmitting}
                />
                 <UploadCloud className="h-6 w-6 text-muted-foreground" />
              </div>
              {pdfFileName && <p className="text-sm text-muted-foreground mt-1">Selected file: {pdfFileName}</p>}
            </div>


            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="status" className="text-foreground">Status</Label>
                <Select
                  value={status}
                  onValueChange={(value: 'draft' | 'published' | 'archived') => setStatus(value)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="!bg-input border-border focus:border-primary focus:ring-primary cursor-pointer">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="draft" className='cursor-pointer'>Draft</SelectItem>
                    <SelectItem value="published" className='cursor-pointer'>Published</SelectItem>
                    <SelectItem value="archived" className='cursor-pointer'>Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2 pb-4">
                <Label className="text-foreground">Tags (Optional)</Label>
                <div className="flex flex-wrap gap-2 p-3 border border-input rounded-md min-h-[40px] bg-background">
                    {allTags.length > 0 ? allTags.map(tag => (
                        <Button
                            type="button"
                            key={tag.id}
                            variant={selectedTagIds.includes(tag.id) ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleTagSelection(String(tag.id))}
                            className={`transition-all cursor-pointer ${selectedTagIds.includes(tag.id) ? '!bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent/10'}`}
                            disabled={isSubmitting}
                        >
                            {tag.name}
                        </Button>
                    )) : <p className="text-sm text-muted-foreground">No tags available or loading...</p>}
                </div>
            </div>

          </CardContent>
          <CardFooter className="border-t border-border/30 pt-6">
            <Button type="submit" className="w-full md:w-auto !bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading & Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" /> Save Research Item
                </>
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default CreateResearchPage;
